import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { useTheme } from '../../contexts/ThemeContext'
import { bucketLabeller, yearLabel } from './series'
import type { AkseldriftTidsserie } from './types'

// Blue / orange: told apart by lightness as well as hue
const COLORS = { '2wd': '#0e7490', '4wd': '#ea580c' }

const pct = (v: number) => `${v.toLocaleString('nb-NO', { maximumFractionDigits: 1 })} %`

export default function AkseldriftChart({ series }: { series: AkseldriftTidsserie }) {
  const { t } = useTranslation('teknisk')
  const { isDark } = useTheme()

  const data = useMemo(() => {
    const label = bucketLabeller(series.granularity)
    return series.points.map(p => ({
      label: label(p.bucket),
      share_2wd: p.share_2wd,
      share_4wd: p.share_4wd,
      prev_2wd: p.previous?.share_2wd ?? null,
      prev_4wd: p.previous?.share_4wd ?? null,
    }))
  }, [series])

  const drive2 = t('akseldrift.drive.2wd')
  const drive4 = t('akseldrift.drive.4wd')
  const { current, previous } = series.periods
  const year = yearLabel(current.start_date, current.end_date)
  const previousYear = yearLabel(previous.start_date, previous.end_date)
  const axisColor = isDark ? '#94a3b8' : '#64748b'
  const gridColor = isDark ? '#334155' : '#e2e8f0'

  return (
    <div className="card p-4">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
        {series.granularity === 'month' ? t('akseldrift.chartTitleMonthly') : t('akseldrift.chartTitleDaily')}
      </h3>
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
        {series.previous_available ? t('akseldrift.chartLegendHint') : t('akseldrift.chartNoComparison')}
      </p>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
            <XAxis dataKey="label" stroke={axisColor} fontSize={12} />
            <YAxis domain={[0, 100]} tickFormatter={(v: number) => `${v} %`} stroke={axisColor} fontSize={12} width={48} />
            <Tooltip formatter={(v: number) => pct(v)} />
            <Legend />
            <Line type="monotone" dataKey="share_2wd" name={t('akseldrift.seriesName', { drive: drive2, year })}
              stroke={COLORS['2wd']} strokeWidth={2} dot={false} connectNulls />
            <Line type="monotone" dataKey="share_4wd" name={t('akseldrift.seriesName', { drive: drive4, year })}
              stroke={COLORS['4wd']} strokeWidth={2} dot={false} connectNulls />
            {series.previous_available && (
              <Line type="monotone" dataKey="prev_2wd" name={t('akseldrift.seriesName', { drive: drive2, year: previousYear })}
                stroke={COLORS['2wd']} strokeDasharray="6 4" strokeOpacity={0.6} dot={false} connectNulls />
            )}
            {series.previous_available && (
              <Line type="monotone" dataKey="prev_4wd" name={t('akseldrift.seriesName', { drive: drive4, year: previousYear })}
                stroke={COLORS['4wd']} strokeDasharray="6 4" strokeOpacity={0.6} dot={false} connectNulls />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}
